import { useEffect, useRef, useState } from 'react';
import './ImageWithLoader.css';

const MIN_LOADER_MS = 200;

export default function ImageWithLoader({
  src,
  alt,
  className,
  wrapperClassName,
  loaderClassName,
  loading,
  decoding,
  onLoad,
  onError,
  ...imgProps
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const loadStartRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (!src) return;
    setIsLoaded(false);
    setHasError(false);
    loadStartRef.current = Date.now();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [src]);

  if (!src) return null;

  const finalizeLoad = (event, nextHasError) => {
    const elapsed = Date.now() - loadStartRef.current;
    const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
    const complete = () => {
      setIsLoaded(true);
      setHasError(nextHasError);
      if (nextHasError) {
        onError?.(event);
      } else {
        onLoad?.(event);
      }
    };

    if (remaining > 0) {
      timeoutRef.current = setTimeout(complete, remaining);
    } else {
      complete();
    }
  };

  const handleLoad = (event) => {
    finalizeLoad(event, false);
  };

  const handleError = (event) => {
    finalizeLoad(event, true);
  };

  const wrapperClasses = [
    'ImageWithLoader',
    isLoaded ? 'is-loaded' : 'is-loading',
    hasError ? 'is-error' : '',
    wrapperClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const imageClasses = ['ImageWithLoader__image', className]
    .filter(Boolean)
    .join(' ');

  const loaderClasses = ['ImageWithLoader__loader', loaderClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wrapperClasses} aria-busy={!isLoaded}>
      {!isLoaded ? (
        <span className={loaderClasses} aria-hidden="true">
          <span className="ImageWithLoader__spinner" />
        </span>
      ) : null}
      <img
        src={src}
        alt={alt}
        className={imageClasses}
        loading={loading}
        decoding={decoding}
        onLoad={handleLoad}
        onError={handleError}
        {...imgProps}
      />
    </span>
  );
}
